import { useState, useRef, useEffect } from 'react';
import i18n from '@forvalter/i18n/config';
import { showToast } from '@klpkonsern/ui';

import {
    useDeleteReportMappingMutation,
    useTransferReportMutation,
} from '../api/rtkApi';

type UseReportMappingActionsProps = {
    reportId: string;
    isMapped: boolean;
};

type UseReportMappingActionsReturn = {
    deleteRef: React.RefObject<HTMLButtonElement>;
    addRef: React.RefObject<HTMLButtonElement>;
    editRef: React.RefObject<HTMLButtonElement>;
    transferRef: React.RefObject<HTMLButtonElement>;
    showConfirmDialog: boolean;
    showAddModal: boolean;
    showEditModal: boolean;
    showTransferModal: boolean;
    isDeleting: boolean;
    isTransferring: boolean;
    handleDelete: () => void;
    handleConfirmDelete: () => Promise<void>;
    handleCancelDelete: () => void;
    handleAddMapping: () => void;
    handleCloseAddModal: () => void;
    handleEditMapping: () => void;
    handleCloseEditModal: () => void;
    handleTransferMapping: () => void;
    handleConfirmTransfer: () => Promise<void>;
    handleCloseTransferModal: () => void;
};

export const useReportMappingActions = ({
    reportId,
    isMapped,
}: UseReportMappingActionsProps): UseReportMappingActionsReturn => {
    const deleteRef = useRef<HTMLButtonElement>(null);
    const addRef = useRef<HTMLButtonElement>(null);
    const editRef = useRef<HTMLButtonElement>(null);
    const transferRef = useRef<HTMLButtonElement>(null);

    const [showConfirmDialog, setShowConfirmDialog] = useState(false);
    const [showAddModal, setShowAddModal] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showTransferModal, setShowTransferModal] = useState(false);

    const [deleteReportMapping, { isLoading: isDeleting }] =
        useDeleteReportMappingMutation();
    const [transferReport, { isLoading: isTransferring }] =
        useTransferReportMutation();

    useEffect(() => {
        setShowAddModal(false);
        setShowEditModal(false);
        setShowConfirmDialog(false);
        setShowTransferModal(false);
    }, [isMapped]);

    const handleDelete = () => {
        setShowConfirmDialog(true);
    };

    const handleConfirmDelete = async () => {
        try {
            await deleteReportMapping({ reportId }).unwrap();
            setShowConfirmDialog(false);
            showToast({ body: i18n.t('general.saved'), type: 'success' });
        } catch {
            showToast({ body: i18n.t('general.error.message'), type: 'error' });
        }
    };

    const handleCancelDelete = () => {
        setShowConfirmDialog(false);
    };

    const handleAddMapping = () => {
        setShowAddModal(true);
    };

    const handleCloseAddModal = () => {
        setShowAddModal(false);
    };

    const handleEditMapping = () => {
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
    };

    const handleTransferMapping = () => {
        setShowTransferModal(true);
    };

    const handleConfirmTransfer = async () => {
        try {
            await transferReport({ reportId }).unwrap();
            setShowTransferModal(false);
            showToast({
                body: i18n.t('reportMapping.mappingTransfer.success'),
                type: 'success',
            });
        } catch {
            showToast({
                body: i18n.t('reportMapping.error.transferFailed'),
                type: 'error',
            });
        }
    };

    const handleCloseTransferModal = () => {
        if (!isTransferring) {
            setShowTransferModal(false);
        }
    };

    return {
        deleteRef,
        addRef,
        editRef,
        transferRef,
        showConfirmDialog,
        showAddModal,
        showEditModal,
        showTransferModal,
        isDeleting,
        isTransferring,
        handleDelete,
        handleConfirmDelete,
        handleCancelDelete,
        handleAddMapping,
        handleCloseAddModal,
        handleEditMapping,
        handleCloseEditModal,
        handleTransferMapping,
        handleConfirmTransfer,
        handleCloseTransferModal,
    };
};
